import {
   Archive03Icon,
   PinIcon,
   TickDouble02Icon,
   UndoIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { formatCurrency } from "@unfiddle/core/currency"
import { formatDate } from "@unfiddle/core/date"
import { makeShortId } from "@unfiddle/core/id"
import { formatOrderDate } from "@unfiddle/core/order/utils"
import { Badge } from "@unfiddle/ui/components/badge"
import { Button } from "@unfiddle/ui/components/button"
import { Card } from "@unfiddle/ui/components/card"
import { DrawerTrigger } from "@unfiddle/ui/components/drawer"
import { Expandable } from "@unfiddle/ui/components/expandable"
import { Icons } from "@unfiddle/ui/components/icons"
import {
   Menu,
   MenuItem,
   MenuPopup,
   MenuTrigger,
} from "@unfiddle/ui/components/menu"
import { Toggle } from "@unfiddle/ui/components/toggle"
import {
   Tooltip,
   TooltipPopup,
   TooltipTrigger,
} from "@unfiddle/ui/components/tooltip"
import * as React from "react"
import { ImagesCarousel } from "@/attachment/components/images-carousel"
import { useAuth } from "@/auth/hooks"
import { ChatLink } from "@/chat/components/chat-link"
import { ClientSeverityIcon } from "@/client/components/client-severity-icon"
import {
   Header,
   HeaderBackButton,
   HeaderTitle,
} from "@/layout/components/header"
import { MainScrollArea } from "@/layout/components/main"
import { useCreateOrderAssignee } from "@/order/assignee/mutations/use-create-order-assignee"
import { useDeleteOrderAssignee } from "@/order/assignee/mutations/use-delete-order-assignee"
import { ArchiveOrderAlert } from "@/order/components/archive-order-alert"
import { DeleteOrderAlert } from "@/order/delete/delete-order-alert"
import { useDeleteOrder } from "@/order/delete/use-delete-order"
import { useOrder } from "@/order/hooks"
import { CreateOrderItem } from "@/order/item/components/create-order-item"
import { useOrderUnreadCount } from "@/order/message/read/queries"
import { UpdateOrder } from "@/order/update/update-order"
import { useUpdateOrder } from "@/order/update/use-update-order"
import { CreateProcurement } from "@/procurement/create/create-procurement"
import { CreateAnalog } from "@/routes/_authed/$workspaceId/_layout/(order)/-components/create-analog"
import { OrderItem } from "@/routes/_authed/$workspaceId/_layout/(order)/-components/order-item"
import { Procurement } from "@/routes/_authed/$workspaceId/_layout/(order)/-components/procurement"
import { SeverityCombobox } from "@/routes/_authed/$workspaceId/_layout/(order)/-components/severity-combobox"
import { StatusCombobox } from "@/routes/_authed/$workspaceId/_layout/(order)/-components/status-combobox"
import { useSocket } from "@/socket"
import { trpc } from "@/trpc"
import { SuspenseBoundary } from "@/ui/components/suspense-boundary"
import { UserAvatar } from "@/user/components/user-avatar"

export const Route = createFileRoute(
   "/_authed/$workspaceId/_layout/(order)/order/$orderId/_layout/",
)({
   component: RouteComponent,
})

function RouteComponent() {
   const params = Route.useParams()
   const navigate = useNavigate()
   const auth = useAuth()
   const order = useOrder()
   const update = useUpdateOrder()
   const createAssignee = useCreateOrderAssignee()
   const deleteAssignee = useDeleteOrderAssignee()
   const socket = useSocket()
   const queryClient = useQueryClient()

   const pressed = order.assignees.some((a) => a.user.id === auth.user.id)

   const imageAttachments = order.attachments.filter(
      (attachment) =>
         attachment.type.startsWith("image/") &&
         !attachment.name.endsWith(".svg"),
   )

   const unreadCount = useOrderUnreadCount(order.id)

   return (
      <>
         <Header className="md:flex md:px-1.75">
            <HeaderBackButton
               className={"mr-1.5 md:flex"}
               onClick={() => navigate({ to: "/$workspaceId", params })}
            />
            <HeaderTitle className="line-clamp-1">
               <span className="md:hidden">Замовлення</span>{" "}
               {makeShortId(order.shortId)}
            </HeaderTitle>
            <Actions />
            <div className="ml-auto flex items-center gap-1.5 max-md:hidden">
               <ChatLink
                  unreadCount={unreadCount}
                  to="/$workspaceId/order/$orderId/chat"
                  params={params}
               />
               <Tooltip>
                  <TooltipTrigger
                     render={
                        <Toggle
                           pressed={pressed}
                           render={(props, state) => (
                              <Button
                                 {...props}
                                 kind={"icon"}
                                 variant={"secondary"}
                                 onClick={() => {
                                    if (state.pressed)
                                       return deleteAssignee.mutate({
                                          orderId: order.id,
                                          userId: auth.user.id,
                                          workspaceId: auth.workspace.id,
                                       })

                                    createAssignee.mutate({
                                       orderId: order.id,
                                       userId: auth.user.id,
                                       workspaceId: auth.workspace.id,
                                    })
                                 }}
                              >
                                 {state.pressed ? (
                                    <HugeiconsIcon icon={UndoIcon} />
                                 ) : (
                                    <HugeiconsIcon icon={PinIcon} />
                                 )}
                              </Button>
                           )}
                        />
                     }
                  />
                  <TooltipPopup>
                     {pressed ? "Залишити" : "Зайняти"}
                  </TooltipPopup>
               </Tooltip>
               <CreateAnalog />
            </div>
         </Header>
         <MainScrollArea>
            <Expandable expanded={order.deletedAt !== null}>
               <Badge
                  variant={"destructive"}
                  className="mb-1.5 text-base"
               >
                  <HugeiconsIcon
                     icon={Archive03Icon}
                     className="mb-0.5 inline-block size-5"
                  />
                  Замовлення у архіві
               </Badge>
            </Expandable>
            <SuspenseBoundary
               fallback={null}
               errorComponent={null}
            >
               <TotalProfit />
            </SuspenseBoundary>
            <div className="mt-2 mb-3 flex gap-2">
               <p className="font-semibold text-xl md:text-2xl">{order.name}</p>
               <ChatLink
                  unreadCount={unreadCount}
                  to="/$workspaceId/order/$orderId/chat"
                  params={params}
                  className="-mt-1.5 ml-auto md:hidden"
               />
            </div>
            <p className="mb-1 whitespace-pre-wrap">{order.note}</p>
            <ImagesCarousel
               className="mb-1"
               subjectId={order.id}
               images={imageAttachments}
               onDelete={() => {
                  socket.order.send({
                     action: "delete_attachment",
                     senderId: auth.user.id,
                     orderId: order.id,
                     workspaceId: auth.workspace.id,
                  })
                  queryClient.invalidateQueries(
                     trpc.order.one.queryOptions({
                        orderId: order.id,
                        workspaceId: auth.workspace.id,
                     }),
                  )
               }}
            />
            <div className="mt-3 mb-3 flex gap-1 lg:hidden">
               <StatusCombobox />
               <SeverityCombobox />
            </div>
            <div className="mb-6 grid grid-cols-[40%_1fr] gap-y-4 lg:hidden">
               <section className="group/section">
                  <p className="text-muted text-sm">Ціна</p>
                  <p className="mt-1.5 font-medium font-mono text-lg">
                     {order.sellingPrice
                        ? formatCurrency(order.sellingPrice, {
                             currency: order.currency,
                          })
                        : "—"}
                  </p>
               </section>
               <section className="group/section">
                  <p className="text-muted text-sm">Термін постачання</p>
                  <p className="mt-1.5">
                     {order.deliversAt ? formatDate(order.deliversAt) : "—"}
                  </p>
               </section>
               <section className="group/section">
                  <p className="text-muted text-sm">Клієнт</p>
                  <p className="mt-1.5 flex items-center gap-2">
                     {order.client ? (
                        <>
                           <ClientSeverityIcon
                              className="-mb-0.5"
                              severity={order.client.severity}
                           />
                           {order.client.name}
                        </>
                     ) : (
                        "—"
                     )}
                  </p>
               </section>
               <section className="group/section">
                  <p className="text-muted text-sm">Створене</p>
                  <p className="mt-2 flex items-center gap-2">
                     <UserAvatar
                        size={22}
                        user={order.creator}
                     />
                     {order.creator.name} —{" "}
                     {new Date(order.createdAt).getDate() ===
                     new Date().getDate() ? (
                        <span className="max-md:hidden">Сьогодні о</span>
                     ) : null}
                     {formatOrderDate(order.createdAt)}
                  </p>
               </section>
            </div>
            <div className="mt-5">
               <div className="mb-2 flex items-center justify-between">
                  <p className="font-medium text-lg">Товари</p>
                  <CreateOrderItem>
                     <DrawerTrigger
                        render={
                           <Button
                              variant={"secondary"}
                              size="sm"
                           >
                              <HugeiconsIcon icon={Icons.plus} />
                              Додати
                           </Button>
                        }
                     />
                  </CreateOrderItem>
               </div>
               <div className="relative">
                  {order.items.map((item) => (
                     <OrderItem
                        key={item.id}
                        item={item}
                     />
                  ))}
               </div>
            </div>
            <div className="relative mt-5 min-h-62.5">
               <div className="my-2 flex items-center justify-between">
                  <p className="font-medium text-lg">Закупівлі</p>
                  <CreateProcurement />
               </div>
               <SuspenseBoundary>
                  <Procurements />
               </SuspenseBoundary>
            </div>
            {order.analogs.length === 0 ? null : (
               <div className="mt-5">
                  <div className="my-2 flex items-center justify-between">
                     <p className="font-medium text-lg">Аналоги</p>
                  </div>
                  <div className="mb-4 flex flex-wrap gap-1">
                     {order.analogs.map((name, idx) => (
                        <Card
                           key={name}
                           className="group/analog before:mask-l-from-8 relative flex items-center px-3 py-1.5 before:pointer-events-none before:absolute before:inset-0 before:z-1 before:bg-background before:opacity-0 before:transition-opacity hover:before:opacity-100"
                        >
                           {name}
                           <Button
                              className="absolute right-0.75 z-2 opacity-0 transition-opacity group-hover/analog:opacity-100"
                              size={"sm"}
                              kind={"icon"}
                              variant={"ghost"}
                              onClick={(e) => {
                                 e.stopPropagation()
                                 update.mutate({
                                    orderId: order.id,
                                    workspaceId: params.workspaceId,
                                    analogs: order.analogs.filter(
                                       (_, existingIdx) => existingIdx !== idx,
                                    ),
                                 })
                              }}
                           >
                              <HugeiconsIcon
                                 icon={Icons.trash}
                                 className="size-4"
                              />
                           </Button>
                        </Card>
                     ))}
                  </div>
               </div>
            )}
         </MainScrollArea>
      </>
   )
}

function TotalProfit() {
   const params = Route.useParams()
   const order = useOrder()
   const query = useSuspenseQuery(trpc.procurement.list.queryOptions(params))
   const procurements = query.data

   const totalProfit = procurements.reduce(
      (acc, p) =>
         acc + ((order.sellingPrice ?? 0) - p.purchasePrice) * p.quantity,
      0,
   )

   return (
      <Expandable expanded={order.status === "successful"}>
         <Badge
            variant={"success"}
            className="mb-1.5 text-base"
         >
            <HugeiconsIcon
               icon={TickDouble02Icon}
               className="size-4.5 md:size-5"
            />
            Замовлення успішне. Профіт:{" "}
            {`${formatCurrency(totalProfit as number, {
               currency: order.currency,
            })}`}{" "}
         </Badge>
      </Expandable>
   )
}

function Actions() {
   const params = Route.useParams()
   const auth = useAuth()
   const order = useOrder()
   const update = useUpdateOrder()
   const deleteItem = useDeleteOrder()

   const [editOpen, setEditOpen] = React.useState(false)
   const [archiveAlertOpen, setArchiveAlertOpen] = React.useState(false)
   const [deleteAlertOpen, setDeleteAlertOpen] = React.useState(false)
   const menuTriggerRef = React.useRef<HTMLButtonElement>(null)

   return (
      <>
         <Menu>
            <MenuTrigger
               render={
                  <Button
                     variant={"ghost"}
                     kind={"icon"}
                     className="md:ml-2"
                  >
                     <HugeiconsIcon
                        icon={Icons.ellipsisHorizontal}
                        className="size-5.5"
                     />
                  </Button>
               }
            />
            <MenuPopup align="start">
               <MenuItem
                  onClick={() => {
                     setEditOpen(true)
                  }}
               >
                  <HugeiconsIcon icon={Icons.pencil} />
                  Редагувати
               </MenuItem>
               {order.deletedAt === null ? (
                  <MenuItem
                     destructive
                     onClick={() => setArchiveAlertOpen(true)}
                  >
                     <HugeiconsIcon icon={Archive03Icon} />
                     Архівувати
                  </MenuItem>
               ) : (
                  <MenuItem
                     onClick={() =>
                        update.mutate({
                           orderId: order.id,
                           workspaceId: params.workspaceId,
                           deletedAt: null,
                        })
                     }
                  >
                     <HugeiconsIcon icon={UndoIcon} />
                     Відновити
                  </MenuItem>
               )}
               {order.deletedAt &&
               (auth.workspace.role === "owner" ||
                  auth.workspace.role === "admin") ? (
                  <MenuItem
                     destructive
                     onClick={() => setDeleteAlertOpen(true)}
                  >
                     <HugeiconsIcon icon={Icons.trash} />
                     Видалити
                  </MenuItem>
               ) : null}
            </MenuPopup>
         </Menu>
         <UpdateOrder
            open={editOpen}
            setOpen={setEditOpen}
            orderId={order.id}
            finalFocus={menuTriggerRef}
         />
         <ArchiveOrderAlert
            open={archiveAlertOpen}
            onOpenChange={setArchiveAlertOpen}
            orderName={order.name}
            finalFocus={menuTriggerRef}
            action={() =>
               update.mutate({
                  orderId: order.id,
                  workspaceId: params.workspaceId,
                  deletedAt: new Date(),
               })
            }
         />
         <DeleteOrderAlert
            open={deleteAlertOpen}
            onOpenChange={setDeleteAlertOpen}
            orderName={order.name}
            finalFocus={menuTriggerRef}
            action={() =>
               deleteItem.mutate({
                  orderId: order.id,
                  workspaceId: params.workspaceId,
               })
            }
         />
      </>
   )
}

function Procurements() {
   const params = Route.useParams()
   const query = useSuspenseQuery(trpc.procurement.list.queryOptions(params))

   if (query.data.length === 0)
      return <p className="font-medium text-muted">Ще немає закупівель.</p>

   return (
      <Card className="relative mt-2 p-0">
         {query.data.map((p) => (
            <Procurement
               key={p.id}
               procurement={p}
            />
         ))}
      </Card>
   )
}
